
import React, { PureComponent } from 'react';

import { translate } from '../../../base/i18n';
import { MEDIA_TYPE } from '../../../base/media';
import { PARTICIPANT_ROLE, getLocalParticipant, getParticipantById } from '../../../base/participants';
import { connect } from '../../../base/redux';
import {
    getScreenShareTrack,
    getTrackByMediaTypeAndParticipant,
    isLocalTrackMuted,
    isRemoteTrackMuted
} from '../../../base/tracks';
import { MultiSessionIndicator } from '../../../session-persistence';
import { LAYOUTS, getCurrentLayout } from '../../../video-layout';

import ModeratorIndicator from './ModeratorIndicator';
import ScreenShareIndicator from './ScreenShareIndicator';
import VideoMutedIndicator from './VideoMutedIndicator';

declare var interfaceConfig: Object;

/**
 * The type of the React {@code Component} props of {@link StatusIndicators}.
 */
type Props = {

    /**
     * The current layout of the filmstrip.
     */
    _currentLayout: string,

    /**
     * Indicates if the audio muted indicator should be visible or not.
     */
    _showAudioMutedIndicator: Boolean,

    /**
     * Indicates if the moderator indicator should be visible or not.
     */
    _showModeratorIndicator: Boolean,

    /**
     * Indicates if the screen share indicator should be visible or not.
     */
    _showScreenShareIndicator: Boolean,

    /**
     * Indicates if the video muted indicator should be visible or not.
     */
    _showVideoMutedIndicator: Boolean,

    /**
     * The ID of the participant for which the status bar is rendered.
     */
    participantID: String
};

/**
 * React {@code Component} for showing the status bar in a thumbnail.
 *
 * @augments Component
 */
class StatusIndicators extends PureComponent<Props> {
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const {
            _currentLayout,
            isSharingYoutubeVideo,

            participantID,
            _showModeratorIndicator,
            _showVideoMutedIndicator,
            isParticipantScreenSharing,
            isParticipantSharingWhiteboard,
            isParticipantSharingAudio,
            t,
            isParticipantSharingEtherpad
        } = this.props;
        let tooltipPosition;
        let tooltipValue;

        switch (_currentLayout) {
        case LAYOUTS.TILE_VIEW:
            tooltipPosition = 'right';
            break;
        case LAYOUTS.VERTICAL_FILMSTRIP_VIEW:
            tooltipPosition = 'left';
            break;
        default:
            tooltipPosition = 'top';
        }

        if (isParticipantScreenSharing) {
            tooltipValue = t('videothumbnail.userSharingScreen');
        } else if (isParticipantSharingWhiteboard) {
            tooltipValue = t('videothumbnail.userSharingWhiteBoard');
        } else if (isParticipantSharingEtherpad) {
            tooltipValue = t('videothumbnail.userSharingLivePad');
        } else if (isSharingYoutubeVideo) {
            tooltipValue = t('videothumbnail.userSharingYouTube');
        } else if (isParticipantSharingAudio) {
            tooltipValue = t('videothumbnail.userSharingScreenAudio');
        } else {
            tooltipValue = null;
        }


        return (
            <div>
                {/* { _showAudioMutedIndicator ? <AudioMutedIndicator tooltipPosition = { tooltipPosition } /> : null } */}
                {(tooltipValue !== null) && <ScreenShareIndicator
                    tooltipValue = { tooltipValue } /> }
                { _showVideoMutedIndicator ? <VideoMutedIndicator tooltipPosition = { tooltipPosition } /> : null }
                { _showModeratorIndicator ? <ModeratorIndicator
                    moderatorText = { interfaceConfig?.CHANGE_MODERATOR_NAME }
                    tooltipPosition = { 'bottom-right' } /> : null }
                <MultiSessionIndicator
                    participantID = { participantID }
                    tooltipPosition = { tooltipPosition } />
            </div>
        );
    }
}

/**
 * Maps (parts of) the Redux state to the associated {@code StatusIndicators}'s props.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The own props of the component.
 * @private
 * @returns {{
 *     _currentLayout: string,
 *     _showModeratorIndicator: boolean,
 *     _showVideoMutedIndicator: boolean
 * }}
*/
function _mapStateToProps(state, ownProps) {
    const { participantID } = ownProps;

    // Only the local participant won't have id for the time when the conference is not yet joined.
    const participant = participantID && participantID !== 'local'
        ? getParticipantById(state, participantID)
        : getLocalParticipant(state);

    const tracks = state['features/base/tracks'];
    const screensharetrack = tracks.find(it => it.videoType === 'desktop' && it.mediaType === MEDIA_TYPE.SCREENSHARE && !it.muted);

    const youtubeSharingParticipant = state['features/shared-video']?.participantSharingVideo;
    const whiteboardParticipantId = state['features/genericiframe'].partId;
    const etherpadParticipantId = state['features/etherpad'].partId;
    const screenSharePartId = screensharetrack?.participantId;
    const isShareAudioTrack = tracks.find(it => it.mediaType === MEDIA_TYPE.AUDIO && it.participantId === participantID && it.mhTrack.track.label === 'Tab audio');
    const isSharingAudio = state['features/screen-share']?.isSharingAudio;

    let isVideoMuted = false;
    let isAudioMuted = false;
    let isScreenSharing = false;
    let isSharingYoutubeVideo = false;

    if (participantID === youtubeSharingParticipant) {
        isSharingYoutubeVideo = true;
    }

    if (participant?.local) {
        const screenShareTrack = getScreenShareTrack(tracks, participantID);

        isVideoMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO);
        isAudioMuted = isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO);
        isScreenSharing = Boolean(screenShareTrack && !screenShareTrack.muted);

    } else if (participant && !participant.isFakeParticipant) { // remote participants excluding shared video
        const screenShareTrack = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.SCREENSHARE, participantID);

        isScreenSharing = Boolean(screenShareTrack && !screenShareTrack.muted);
        isVideoMuted = isRemoteTrackMuted(tracks, MEDIA_TYPE.VIDEO, participantID);
        isAudioMuted = isRemoteTrackMuted(tracks, MEDIA_TYPE.AUDIO, participantID);
    }

    return {
        _currentLayout: getCurrentLayout(state),
        _showAudioMutedIndicator: isAudioMuted,
        _showModeratorIndicator:
            !interfaceConfig.DISABLE_FOCUS_INDICATOR && participant && participant.role === PARTICIPANT_ROLE.MODERATOR,
        _showScreenShareIndicator: isScreenSharing,
        _showVideoMutedIndicator: Boolean(participant && !participant.isFakeParticipant && isVideoMuted),
        isParticipantScreenSharing: screenSharePartId === participantID,
        isParticipantSharingWhiteboard: whiteboardParticipantId === participantID,
        isParticipantSharingEtherpad: etherpadParticipantId === participantID,
        isParticipantSharingAudio: isSharingAudio && isShareAudioTrack !== undefined,
        isSharingYoutubeVideo
    };
}

export default connect(_mapStateToProps)(translate(StatusIndicators));
